import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Cookie-based auth. Tokens live in httpOnly cookies (access_token,
 * refresh_token) set by the backend. Frontend never touches raw JWTs.
 *
 * State machine for `user`:
 *   null  → still checking session (initial mount)
 *   false → confirmed logged-out
 *   {…}   → logged-in user object from /auth/me
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Two-factor challenge — surface it upstream so the UI can prompt for a TOTP code.
    if (data?.requires_2fa) return data;
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
